import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { CreateCertificateCriteriaAction } from "../../Redux/Actions/RewardAction";

interface CreateCertificateCriteriaModalProps {
  open: boolean;
  onClose: () => void;
  certificateId: string;
  onSuccess?: () => void;
}

const CreateCertificateCriteriaModal: React.FC<
  CreateCertificateCriteriaModalProps
> = ({ open, onClose, certificateId, onSuccess }) => {
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    criteria_type: "assessment",
    level: "completion",
    assessment_id: "",
    score: "",
    is_active: true,
  });

  const handleChange = (e: any) => {
    const { name, value, checked, type } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const isExcellence = formData?.level === "excellence";
      const levelData: any = {
        assessment_id: parseInt(formData?.assessment_id),
      };

      if (isExcellence) {
        levelData.score = parseInt(formData?.score);
      }

      const criteriaJson = {
        [formData?.criteria_type]: {
          [formData?.level]: levelData,
        },
      };

      const criteriaData = {
        template_id: certificateId,
        name: formData?.name,
        criteria_type: formData?.criteria_type,
        level: formData?.level,
        criteria_json: criteriaJson,
        is_active: formData?.is_active,
      };
      const result = await dispatch(CreateCertificateCriteriaAction(criteriaData));
      
      if (result && result.status === true) {
        onClose();
        setFormData({
          name: "",
          criteria_type: "assessment",
          level: "completion",
          assessment_id: "",
          score: "",
          is_active: true,
        });
        if (onSuccess) {
          onSuccess(); 
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Certificate Award Criteria</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            name="name"
            label="Name"
            value={formData?.name}
            onChange={handleChange}
            fullWidth
            required
            placeholder="Enter criteria name"
          />

          <FormControl fullWidth required>
            <InputLabel>Criteria Type</InputLabel>
            <Select
              name="criteria_type"
              value={formData?.criteria_type}
              onChange={handleChange}
              label="Criteria Type"
            >
              <MenuItem value="assessment">Assessment</MenuItem>
              <MenuItem value="contest">Contest</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel>Level</InputLabel>
            <Select
              name="level"
              value={formData?.level}
              onChange={handleChange}
              label="Level"
            >
              <MenuItem value="participation">Participation</MenuItem>
              <MenuItem value="completion">Completion</MenuItem>
              <MenuItem value="excellence">Excellence</MenuItem>
            </Select>
          </FormControl>

          {formData?.level === "excellence" && (
            <TextField
              name="score"
              label="Score"
              value={formData?.score}
              onChange={handleChange}
              fullWidth
              required
              type="number"
              placeholder="Enter minimum score"
            />
          )}

          <TextField
            name="assessment_id"
            label={`${
              formData?.criteria_type === "assessment"
                ? "Assessment"
                : "Contest"
            } ID`}
            value={formData?.assessment_id}
            onChange={handleChange}
            fullWidth
            required
            type="number"
            placeholder="Enter the ID"
          />

          <FormControlLabel
            control={
              <Switch
                name="is_active"
                checked={formData?.is_active}
                onChange={handleChange}
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !formData?.assessment_id ||
            !formData?.name ||
            (formData?.level === "excellence" && !formData?.score)
          }
          sx={{
            backgroundColor: "#0555c8",
            "&:hover": {
              backgroundColor: "#043c99",
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCertificateCriteriaModal;
